import { useEffect, useMemo, useState } from 'react'
import { fxSalesApi, fxCustomersApi, fxProductsApi } from '../../services/api'
import { getCurrentPositionSafe } from '../../utils/geolocation'
import ExportBar, { ExportColumn } from '../../components/ExportBar'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

interface Customer {
  id: number
  name: string
  phone: string | null
}

interface Product {
  id: number
  name: string
  sale_price: number
  stock_qty: number
  is_active: boolean
}

interface Sale {
  id: number
  customer_id: number
  customer_name: string
  product_id: number
  product_name: string
  quantity: number
  unit_price: number
  total_amount: number
  sale_date: string
  notes: string | null
}

interface CartItem {
  product_id: number
  product_name: string
  quantity: number
  unit_price: number
}

export default function FxSalesPage() {
  const [sales, setSales] = useState<Sale[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [customerId, setCustomerId] = useState<number | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [cart, setCart] = useState<CartItem[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchAll = async () => {
    try {
      const [salesRes, customersRes, productsRes] = await Promise.all([
        fxSalesApi.list(),
        fxCustomersApi.list(),
        fxProductsApi.list(),
      ])
      setSales(salesRes.data.sales || [])
      setCustomers(customersRes.data.customers || [])
      setProducts((productsRes.data.products || []).filter((p: Product) => p.is_active))
    } catch (err) {
      console.error('Error fetching FX sales data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const { sortedData, sortConfig, requestSort } = useSortableData(sales, {
    key: 'sale_date',
    direction: 'desc',
  })

  const exportColumns: ExportColumn<Sale>[] = [
    { key: 'sale_date', label: 'Fecha', format: (s) => new Date(s.sale_date).toLocaleDateString() },
    { key: 'customer_name', label: 'Cliente' },
    { key: 'product_name', label: 'Producto' },
    { key: 'quantity', label: 'Cantidad', format: (s) => String(s.quantity) },
    { key: 'unit_price', label: 'Precio Unitario', format: (s) => `S/ ${s.unit_price.toFixed(2)}` },
    { key: 'total_amount', label: 'Total', format: (s) => `S/ ${s.total_amount.toFixed(2)}` },
  ]

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.unit_price * item.quantity, 0),
    [cart]
  )

  const openNew = () => {
    setCustomerId(null)
    setSelectedProductId(null)
    setQuantity(1)
    setCart([])
    setError('')
    setShowForm(true)
  }

  const handleAddToCart = () => {
    if (!selectedProductId) {
      setError('Selecciona un producto')
      return
    }
    const product = products.find((p) => p.id === selectedProductId)
    if (!product) return
    if (quantity < 1) {
      setError('La cantidad debe ser al menos 1')
      return
    }
    setCart((prev) => [
      ...prev,
      { product_id: product.id, product_name: product.name, quantity, unit_price: product.sale_price },
    ])
    setSelectedProductId(null)
    setQuantity(1)
    setError('')
  }

  const handleRemoveFromCart = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index))
  }

  const handleConfirmSale = async () => {
    if (!customerId) {
      setError('Selecciona un cliente')
      return
    }
    if (cart.length === 0) {
      setError('Agrega al menos un producto')
      return
    }
    setSaving(true)
    setError('')
    try {
      // Silent, non-blocking GPS capture: if permission is denied or the
      // request times out, this resolves to null and the sale is still
      // registered without coordinates.
      const pos = await getCurrentPositionSafe()

      await fxSalesApi.createBulk({
        customer_id: customerId,
        gps_latitude: pos?.latitude ?? null,
        gps_longitude: pos?.longitude ?? null,
        items: cart.map((item) => ({ product_id: item.product_id, quantity: item.quantity })),
      })
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al registrar la venta')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-espresso-800">Ventas — FX Admin</h1>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Nueva Venta
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Nueva Venta</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Cliente *</label>
                <select
                  value={customerId ?? ''}
                  onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                >
                  <option value="">Selecciona un cliente</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.phone ? ` (${c.phone})` : ''}</option>
                  ))}
                </select>
                {customers.length === 0 && (
                  <p className="mt-1 text-xs text-espresso-500">No tienes clientes todavía — agrega uno primero en la pestaña Clientes.</p>
                )}
              </div>

              <div className="bg-cream-100 rounded-lg p-3 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[160px]">
                  <label className="block text-xs font-medium text-espresso-500 mb-1">Producto</label>
                  <select
                    value={selectedProductId ?? ''}
                    onChange={(e) => setSelectedProductId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="">Selecciona un producto</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} — S/ {p.sale_price.toFixed(2)} (stock: {p.stock_qty})</option>
                    ))}
                  </select>
                </div>
                <div className="w-24">
                  <label className="block text-xs font-medium text-espresso-500 mb-1">Cantidad</label>
                  <input
                    type="number" min="1" value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <button
                  type="button" onClick={handleAddToCart}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700"
                >
                  Agregar
                </button>
              </div>

              {cart.length > 0 && (
                <div className="border border-espresso-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-espresso-100">
                    <thead className="bg-cream-100">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-espresso-500 uppercase">Producto</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-espresso-500 uppercase">Cant.</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-espresso-500 uppercase">Subtotal</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-espresso-100">
                      {cart.map((item, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 text-sm text-espresso-800">{item.product_name}</td>
                          <td className="px-3 py-2 text-sm text-espresso-800">{item.quantity}</td>
                          <td className="px-3 py-2 text-sm text-espresso-800">S/ {(item.unit_price * item.quantity).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">
                            <button type="button" onClick={() => handleRemoveFromCart(index)} className="text-red-600 hover:text-red-700 text-sm">Quitar</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-3 py-2 bg-cream-100 text-right text-sm font-semibold text-espresso-800">
                    Total: S/ {cartTotal.toFixed(2)}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100">
                  Cancelar
                </button>
                <button type="button" onClick={handleConfirmSale} disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50">
                  {saving ? 'Guardando...' : 'Confirmar Venta'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {sales.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay ventas registradas todavía.</p>
        </div>
      ) : (
        <>
          <ExportBar data={sales} columns={exportColumns} filename="fx_ventas" title="Ventas FX Admin" />
          <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-espresso-100">
                <thead className="bg-cream-50">
                  <tr>
                    <SortableTh label="Fecha" sortKey="sale_date" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Cliente" sortKey="customer_name" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Producto" sortKey="product_name" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Cantidad" sortKey="quantity" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Total" sortKey="total_amount" sortConfig={sortConfig} onSort={requestSort} />
                  </tr>
                </thead>
                <tbody className="bg-cream-50 divide-y divide-espresso-100">
                  {sortedData.map((s) => (
                    <tr key={s.id} className="hover:bg-cream-100">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">
                        {new Date(s.sale_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{s.customer_name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{s.product_name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{s.quantity}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">S/ {s.total_amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
